import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { BRAND } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";
import LanguageSwitcher, { LANGS } from "@/components/LanguageSwitcher";

const NAV_IDS = ["home", "sobre", "servicos", "projetos", "case", "contatos"];

const Navbar = () => {
  const { t, lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? "border-b border-emerald-500/15 bg-[#07120C]/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 sm:px-10 lg:px-16">
        <a
          href="#home"
          data-testid="nav-logo-link"
          className="flex flex-col items-center leading-none"
          onClick={() => setOpen(false)}
        >
          <img
            src="/logo.png"
            alt="Eco Build Group Solution"
            data-testid="nav-logo-image"
            className="h-16 w-auto sm:h-20"
          />
          <span className="mt-1.5 flex w-16 justify-between font-mono-tech text-[9px] font-bold uppercase text-emerald-400 sm:w-20 sm:text-[10px]">
            {t.wordmark.split("").map((letter, i) => (
              <span key={i}>{letter}</span>
            ))}
          </span>
        </a>

        <nav className="hidden items-center gap-6 lg:flex xl:gap-8" data-testid="desktop-nav">
          {NAV_IDS.map((id) => (
            <a
              key={id}
              href={`#${id}`}
              data-testid={`nav-${id}-link`}
              className="font-mono-tech text-xs uppercase tracking-[0.25em] text-slate-300 transition-colors duration-300 hover:text-emerald-400"
            >
              {t.nav[id]}
            </a>
          ))}
          <LanguageSwitcher />
          <a
            href={BRAND.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="group flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#07120C] transition-colors duration-300 hover:bg-emerald-400"
          >
            <MessageCircle className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
            {t.nav.cta}
          </a>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <LanguageSwitcher />
          <button
            data-testid="mobile-menu-toggle"
            className="rounded-lg border border-emerald-500/25 p-2 text-emerald-400"
            onClick={() => setOpen((v) => !v)}
            aria-label={t.nav.menu}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          data-testid="mobile-nav"
          className="border-t border-emerald-500/15 bg-[#07120C]/95 px-6 py-6 backdrop-blur-xl lg:hidden"
        >
          <div className="flex flex-col gap-5">
            {NAV_IDS.map((id) => (
              <a
                key={id}
                href={`#${id}`}
                data-testid={`mobile-nav-${id}-link`}
                className="font-mono-tech text-sm uppercase tracking-[0.25em] text-slate-200 transition-colors hover:text-emerald-400"
                onClick={() => setOpen(false)}
              >
                {t.nav[id]}
              </a>
            ))}
            <div className="flex items-center gap-3 pt-2">
              {LANGS.map(({ id, label, Flag }) => (
                <button
                  key={id}
                  data-testid={`mobile-language-option-${id}`}
                  onClick={() => setLang(id)}
                  aria-label={label}
                  className={`flex items-center gap-2 rounded-full border px-3.5 py-2 transition-colors duration-200 ${
                    lang === id
                      ? "border-emerald-400/70 bg-emerald-500/15"
                      : "border-emerald-500/25 hover:border-emerald-400/50"
                  }`}
                >
                  <Flag className="h-3.5 w-5" />
                  <span className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-300">
                    {id}
                  </span>
                </button>
              ))}
            </div>
            <a
              href={BRAND.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-whatsapp-cta"
              className="mt-2 flex w-fit items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#07120C]"
            >
              <MessageCircle className="h-4 w-4" />
              {t.nav.cta}
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
